import React from 'react';
import { Link } from 'react-router-dom';
import { formatGearLocationLabel } from './gearReviewSummaries';
import { ProfileChip } from './ProfileLink';
import {
  GEAR_IMAGE_PLACEHOLDER,
  OWNER_SHARE_PERCENT,
  PLATFORM_FEE_PERCENT,
} from './rentalPayments';
import { renderStarRating } from './reviewUtils';

function HomeHero({ inventoryCount = 0, user = null }) {
  const listDestination = user ? '/your-hangar' : '/create-profile';

  return (
    <section className="home-hero home-hero-with-image">
      <div className="home-hero-content">
        <h1 className="home-hero-title">Adventure starts here</h1>
        <div className="home-hero-copy">
          <p className="home-hero-subtitle">
            Plan the trip. Rent the gear. Ask someone who knows the trail.
          </p>
          <p className="home-hero-tagline">Explore more. Spend less. Own less.</p>
          <div className="home-hero-actions">
            <a href="#gear-search" className="home-hero-cta home-hero-cta-primary">
              <span className="home-hero-cta-label-full">Search for your trip</span>
              <span className="home-hero-cta-label-short">Search</span>
            </a>
            <Link to={listDestination} className="home-hero-cta home-hero-cta-secondary">
              <span className="home-hero-cta-label-full">List your gear</span>
              <span className="home-hero-cta-label-short">List</span>
            </Link>
            <a href="#local-experts" className="home-hero-cta home-hero-cta-tertiary">
              <span className="home-hero-cta-label-full">Ask a local</span>
              <span className="home-hero-cta-label-short">Ask a local</span>
            </a>
          </div>
          {inventoryCount > 0 && (
            <p className="home-hero-inventory">
              {inventoryCount} {inventoryCount === 1 ? 'item' : 'items'} currently listed
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export function TrustStrip() {
  const items = [
    {
      icon: '🔒',
      title: 'Secure payments',
      text: 'Rentals are processed through Stripe on the platform.',
      link: '/faq#payments',
    },
    {
      icon: '⭐',
      title: 'Community reviews',
      text: 'Renter feedback appears on gear listings after completed rentals.',
      link: '/faq#reviews',
    },
    {
      icon: '🧭',
      title: 'In-app messaging',
      text: 'Coordinate pickup, questions, and local tips with owners directly.',
      link: '/faq#payments',
    },
    {
      icon: '🏕️',
      title: 'Condition holds',
      text: 'Optional security hold (up to $2,500) released after a good return.',
      link: '/faq#condition-holds',
    },
  ];

  return (
    <section className="home-trust-strip" aria-label="Why renters and owners trust ATTACK! Your Adventure">
      {items.map((item) => (
        <article key={item.title} className="home-trust-item">
          <span className="home-trust-icon" aria-hidden="true">{item.icon}</span>
          <h3>{item.title}</h3>
          <p>{item.text}</p>
          {item.link && (
            <Link to={item.link} className="home-trust-link">
              Learn more
            </Link>
          )}
        </article>
      ))}
    </section>
  );
}

function getCategoryIcon(name) {
  const normalized = String(name || '')
    .toLowerCase()
    .replace(/\s*\/\s*/g, '/');

  if (normalized.includes('kayak') || normalized.includes('canoe')) return '🛶';
  if (normalized.includes('paddle') || normalized.includes('sup')) return '🏄';
  if (normalized.includes('surf')) return '🏄';
  if (normalized.includes('dive') || normalized.includes('snorkel')) return '🤿';
  if (normalized.includes('fish')) return '🎣';
  if (normalized.includes('boat') || normalized.includes('sail')) return '⛵';
  if (normalized.includes('water')) return '🛶';

  if (normalized.includes('tent')) return '⛺';
  if (normalized.includes('camp')) return '🏕️';

  if (normalized.includes('hike') || normalized.includes('backpack') || normalized.includes('trek')) return '🥾';
  if (normalized.includes('climb') || normalized.includes('boulder')) return '🧗';
  if (normalized.includes('mountain')) return '⛰️';

  if (normalized.includes('snowboard')) return '🏂';
  if (normalized.includes('snow') || normalized.includes('ski')) return '🎿';
  if (normalized.includes('sled')) return '🛷';

  if (normalized.includes('bike') || normalized.includes('cycle') || normalized.includes('bicycle')) return '🚵';
  if (normalized.includes('scooter')) return '🛴';

  if (normalized.includes('utv') || normalized.includes('atv') || normalized.includes('off-road')) return '🏎️';
  if (normalized.includes('motorcycle') || normalized.includes('dirt bike')) return '🏍️';
  if (normalized.includes('rv') || normalized.includes('camper')) return '🚐';
  if (normalized.includes('vehicle') || normalized.includes('truck') || normalized.includes('jeep')) return '🛻';

  if (normalized.includes('aviation') || normalized.includes('plane') || normalized.includes('aircraft')) return '🛩️';
  if (normalized.includes('paraglid') || normalized.includes('hang glid')) return '🪂';

  if (normalized.includes('hunt') || normalized.includes('archery')) return '🏹';
  if (normalized.includes('disc golf') || normalized.includes('frisbee')) return '🥏';
  if (normalized.includes('golf')) return '⛳';
  if (normalized.includes('photo') || normalized.includes('camera') || normalized.includes('optic')) return '🔭';

  return '🎒';
}

export function PopularCategories({ categories = [], onSelectCategory }) {
  if (!categories.length) return null;

  const sorted = [...categories].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''))
  );

  return (
    <section className="home-categories" aria-label="Browse by category">
      <h2 className="home-section-title">Browse by category</h2>
      <p className="home-section-lead">
        Tap a category to pre-fill search, then add your location or search nationwide.
      </p>
      <div className="home-categories-grid">
        {sorted.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className="home-category-chip"
            onClick={() => onSelectCategory(cat.id)}
          >
            <span className="home-category-icon" aria-hidden="true">
              {getCategoryIcon(cat.name)}
            </span>
            <span>{cat.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function BenefitTiles() {
  const benefits = [
    {
      icon: '🎒',
      title: 'Try the activity first',
      text: 'Rent what you need for a trip instead of buying gear that sits in the garage.',
    },
    {
      icon: '🛶',
      title: 'Share what you love',
      text: 'List gear you already use so someone else can try your favorite activity — and earn when it sits idle.',
    },
    {
      icon: '🥾',
      title: 'Explore more',
      text: 'Find equipment near your destination, or ask a local expert who knows the trails and put-ins.',
    },
  ];

  return (
    <section className="home-benefits" aria-label="Benefits">
      <div className="home-benefits-grid">
        {benefits.map((benefit) => (
          <article key={benefit.title} className="home-benefit-tile">
            <span className="home-benefit-icon" aria-hidden="true">{benefit.icon}</span>
            <h3>{benefit.title}</h3>
            <p>{benefit.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    {
      title: 'Search & request',
      text: 'Find gear near you or nationwide, pick your trip dates, and send a rental request to the owner.',
    },
    {
      title: 'Get approved',
      text: 'The owner reviews your request. Once approved, save your card to secure the booking.',
    },
    {
      title: 'Get outside',
      text: 'Your card is charged 48 hours before the rental starts. Coordinate pickup, ask for a local tip, and go.',
    },
  ];

  return (
    <section id="how-it-works" className="how-it-works" aria-label="How it works">
      <h2 className="how-it-works-title">How it works</h2>
      <div className="how-it-works-grid">
        {steps.map((step, index) => (
          <article key={step.title} className="how-it-works-step">
            <span className="how-it-works-number">{index + 1}</span>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
          </article>
        ))}
      </div>
      <p className="how-it-works-fee-note">
        Our platform fee is only {PLATFORM_FEE_PERCENT}% of the rental cost — and we plan to keep it that way.
        Owners receive {OWNER_SHARE_PERCENT}% when the rental is charged.{' '}
        <Link to="/faq#owners">Learn more</Link>
      </p>
    </section>
  );
}

export function FeaturedGearSection({ gear = [], categories = [], user, loading }) {
  return (
    <section className="home-featured" aria-label="Featured gear">
      <h2 className="home-section-title">Adventure-ready gear</h2>
      {loading ? (
        <p className="home-section-lead">Loading listings…</p>
      ) : gear.length > 0 ? (
        <p className="home-section-lead">
          Real listings from owners who are ready to share their gear — and their favorite places to use it.
        </p>
      ) : (
        <div className="home-featured-empty">
          <p>New listings are added as owners join. Check back soon — or be among the first to list your gear.</p>
          <Link to={user ? '/your-hangar' : '/create-profile'} className="home-featured-empty-link">
            List your gear
          </Link>
        </div>
      )}
      {!loading && gear.length > 0 && (
        <div className="home-featured-list">
          {gear.map((item) => (
            <FeaturedGearCard key={item.id} gear={item} categories={categories} />
          ))}
        </div>
      )}
    </section>
  );
}

function FeaturedGearCard({ gear, categories }) {
  const categoryName =
    categories.find((cat) => cat.id === gear.category)?.name || gear.category || 'Gear';
  const locationLabel = formatGearLocationLabel(gear.location);
  const reviewSummary = gear.reviewSummary;

  return (
    <Link to={`/gear/${gear.id}`} className="home-featured-card">
      <img
        src={gear.mainPicture || GEAR_IMAGE_PLACEHOLDER}
        alt={gear.name}
        className="home-featured-card-image"
      />
      <div className="home-featured-card-body">
        <h3>{gear.name}</h3>
        {reviewSummary?.count > 0 && (
          <p className="home-featured-card-rating">
            {renderStarRating(reviewSummary.rating)}
            <span style={{ marginLeft: '6px', color: '#6b7280' }}>
              ({reviewSummary.count})
            </span>
          </p>
        )}
        <p className="home-featured-card-meta">
          {categoryName} · {locationLabel}
        </p>
        {gear.ownerId && (
          <p className="home-featured-card-host">
            <ProfileChip userId={gear.ownerId} label="Hosted by" />
          </p>
        )}
        <p className="home-featured-card-price">
          ${gear.pricePerDay || '—'}<span>/day</span>
        </p>
      </div>
    </Link>
  );
}

export function OwnerListCTA({ user }) {
  const destination = user ? '/your-hangar' : '/create-profile';
  const ctaLabel = user ? 'Go to Your Hangar' : 'Create account & list gear';

  return (
    <section className="home-owner-cta" aria-label="List your gear">
      <div className="home-owner-cta-inner">
        <h2>Help someone try your favorite activity</h2>
        <p>
          List gear you already use, set your price, and approve requests on your schedule. You earn when it sits idle —
          and someone else gets outside. Payments stay on the platform; we cover payment processing.
        </p>
        <p className="home-owner-example">
          Example only: $50/day × 12 rental days ≈ $600/year gross — you keep {OWNER_SHARE_PERCENT}%
          after our {PLATFORM_FEE_PERCENT}% platform fee.
        </p>
        <Link to={destination} className="home-owner-cta-button">
          {ctaLabel}
        </Link>
      </div>
    </section>
  );
}

export default HomeHero;